import { applyUserTranslationsAllTypes } from "@/lib/utils";
import type {
  Folders,
  ProjectAU,
  FederPAIOperaciones,
  FederPAIContratos,
  FederPAIProjectActuacion,
  FederPAIProject,
  FederPAIIndicator,
} from "@/types/types";

type GenericProject = { codigo?: string; nombre?: string; translations?: any[] };
type GenericTransaccion = { codigo?: string };
type GenericIndicadorAU = {
  codigo?: string;
  descripcion?: string;
  translations?: any[];
};

type TaxonomyFile = {
  taxonomies?: {
    rco?: Record<string, string>;
    rcr?: Record<string, string>;
  };
};

const normalize = <T,>(value: unknown, key: string): T[] | null => {
  if (Array.isArray(value)) return value as T[];
  if (typeof value === "object" && value !== null && key in (value as any)) {
    return [value as T];
  }
  return null;
};

const localeMap: Record<string, string> = {
  es: "es-ES",
  en: "en-US",
  ca: "ca-ES",
  eu: "eu-ES",
  ga: "ga-ES",
};

const loadTaxonomies = async (
  userLang: string
): Promise<TaxonomyFile["taxonomies"]> => {
  const locale = localeMap[userLang] ?? "es-ES";

  try {
    const res = await fetch(`/idioma/${locale}.json`);
    if (!res.ok) return {};
    const json: TaxonomyFile = await res.json();
    return json.taxonomies ?? {};
  } catch {
    return {};
  }
};

export const getIndicatorDisplay = async (
  nombre?: string,
  userLang: string = "es"
): Promise<string> => {
  if (!nombre) return "—";

  const clean = nombre.trim();
  const taxonomies = await loadTaxonomies(userLang);

  const rcoMatch = clean.match(/^RCO\s+(.+)$/i);
  if (rcoMatch) {
    const label = nombre ? taxonomies?.rco?.[nombre] : undefined;
    return label ? `${clean} - ${label}` : clean;
  }

  const rcrMatch = clean.match(/^RCR\s+(.+)$/i);
  if (rcrMatch) {
    const label = nombre ? taxonomies?.rcr?.[nombre] : undefined;
    return label ? `${clean} - ${label}` : clean;
  }

  return clean;
};

export const localizeFolder = async (
  folder: Folders,
  userLang: string
): Promise<Folders> => {
  const auNorm = normalize<ProjectAU>((folder as any).proyecto_au, "codigo");
  if (auNorm?.length) {
    const translated = applyUserTranslationsAllTypes<ProjectAU>(
      auNorm,
      userLang,
      ["nombre"]
    );
    const first = translated[0] as any;
    return {
      ...folder,
      proyecto_au: translated as any,
      name: first ? `${first.codigo} - ${first.nombre}` : folder.name,
    };
  }

  const opNorm = normalize<FederPAIOperaciones>(
    (folder as any).feder_operacion,
    "codigo"
  );
  if (opNorm?.length) {
    const translated = applyUserTranslationsAllTypes<FederPAIOperaciones>(
      opNorm,
      userLang,
      ["nombre"]
    );
    const first = translated[0] as any;
    return {
      ...folder,
      feder_operacion: translated as any,
      name: first ? `${first.codigo} - ${first.nombre}` : folder.name,
    };
  }

  const federProjectNorm = normalize<FederPAIProject>(
    (folder as any).feder_proyecto,
    "codigo"
  );
  if (federProjectNorm?.length) {
    const translated = applyUserTranslationsAllTypes<FederPAIProject>(
      federProjectNorm,
      userLang,
      ["nombre"]
    );
    const first = translated[0] as any;
    return {
      ...folder,
      feder_proyecto: translated as any,
      name: first ? `${first.codigo} - ${first.nombre}` : folder.name,
    };
  }

  const federActuacionNorm = normalize<FederPAIProjectActuacion>(
    (folder as any).feder_actuacion,
    "codigo"
  );
  if (federActuacionNorm?.length) {
    const translated = applyUserTranslationsAllTypes<FederPAIProjectActuacion>(
      federActuacionNorm,
      userLang,
      ["nombre"]
    );
    const first = translated[0] as any;
    return {
      ...folder,
      feder_actuacion: translated as any,
      name: first ? `${first.codigo} - ${first.nombre}` : folder.name,
    };
  }

  const contratoNorm = normalize<FederPAIContratos>(
    (folder as any).feder_contrato,
    "codigo"
  );
  if (contratoNorm?.length) {
    const translated = applyUserTranslationsAllTypes<FederPAIContratos>(
      contratoNorm,
      userLang,
      ["denominacion"]
    );
    const first = translated[0] as any;
    return {
      ...folder,
      feder_contrato: translated as any,
      name: first ? `${first.codigo} - ${first.denominacion}` : folder.name,
    };
  }

  const federIndicadorNorm = normalize<FederPAIIndicator>(
    (folder as any).feder_indicador,
    "nombre"
  );
  if (federIndicadorNorm?.length) {
    const first = federIndicadorNorm[0] as any;
    const codigo = first?.codigo ?? "";
    const nombre = first?.nombre ?? "";
    const indicatorLabel = await getIndicatorDisplay(nombre, userLang);

    const label =
      codigo && indicatorLabel
        ? `${codigo} - ${indicatorLabel}`
        : codigo || indicatorLabel || folder.name;

    return {
      ...folder,
      feder_indicador: federIndicadorNorm as any,
      name: label,
    };
  }

  const propioNorm = normalize<GenericProject>(
    (folder as any).proyecto_propio,
    "codigo"
  );
  if (propioNorm?.length) {
    const translated = applyUserTranslationsAllTypes<GenericProject>(
      propioNorm,
      userLang,
      ["nombre"]
    );
    const first = translated[0] as any;
    const code = first?.codigo ?? "";
    const name = first?.nombre ?? "";
    const label =
      code && name ? `${code} - ${name}` : code || name || folder.name;

    return {
      ...folder,
      proyecto_propio: translated as any,
      name: label,
    };
  }

  const otroNorm = normalize<GenericProject>(
    (folder as any).otro_proyecto,
    "codigo"
  );
  if (otroNorm?.length) {
    const translated = applyUserTranslationsAllTypes<GenericProject>(
      otroNorm,
      userLang,
      ["nombre"]
    );
    const first = translated[0] as any;
    const code = first?.codigo ?? "";
    const name = first?.nombre ?? "";
    const label =
      code && name ? `${code} - ${name}` : code || name || folder.name;

    return {
      ...folder,
      otro_proyecto: translated as any,
      name: label,
    };
  }

  const actuacionNorm = normalize<GenericProject>(
    (folder as any).otra_actuacion,
    "codigo"
  );
  if (actuacionNorm?.length) {
    const translated = applyUserTranslationsAllTypes<GenericProject>(
      actuacionNorm,
      userLang,
      ["nombre"]
    );
    const first = translated[0] as any;
    const code = first?.codigo ?? "";
    const name = first?.nombre ?? "";
    const label =
      code && name ? `${code} - ${name}` : code || name || folder.name;

    return {
      ...folder,
      otra_actuacion: translated as any,
      name: label,
    };
  }

  const actuacionPropiaNorm = normalize<GenericProject>(
    (folder as any).actuacion_propia,
    "codigo"
  );
  if (actuacionPropiaNorm?.length) {
    const translated = applyUserTranslationsAllTypes<GenericProject>(
      actuacionPropiaNorm,
      userLang,
      ["nombre"]
    );
    const first = translated[0] as any;
    const code = first?.codigo ?? "";
    const name = first?.nombre ?? "";
    const label =
      code && name ? `${code} - ${name}` : code || name || folder.name;

    return {
      ...folder,
      actuacion_propia: translated as any,
      name: label,
    };
  }

  const actuacionAUNorm = normalize<GenericProject>(
    (folder as any).actuacion_au,
    "codigo"
  );
  if (actuacionAUNorm?.length) {
    const translated = applyUserTranslationsAllTypes<GenericProject>(
      actuacionAUNorm,
      userLang,
      ["nombre"]
    );
    const first = translated[0] as any;
    const code = first?.codigo ?? "";
    const name = first?.nombre ?? "";
    const label =
      code && name ? `${code} - ${name}` : code || name || folder.name;

    return {
      ...folder,
      actuacion_au: translated as any,
      name: label,
    };
  }

  const indicadorAUNorm = normalize<GenericIndicadorAU>(
    (folder as any).indicador_au,
    "codigo"
  );
  if (indicadorAUNorm?.length) {
    const translated = applyUserTranslationsAllTypes<GenericIndicadorAU>(
      indicadorAUNorm,
      userLang,
      ["descripcion"]
    );
    const first = translated[0] as any;
    const code = first?.codigo ?? "";
    const desc = first?.descripcion ?? "";
    const label =
      code && desc ? `${code} - ${desc}` : code || desc || folder.name;

    return {
      ...folder,
      indicador_au: translated as any,
      name: label,
    };
  }

  const transaccionNorm = normalize<GenericTransaccion>(
    (folder as any).otra_transaccion,
    "codigo"
  );
  if (transaccionNorm?.length) {
    const first = transaccionNorm[0] as any;
    const code = first?.codigo ?? "";
    return {
      ...folder,
      otra_transaccion: transaccionNorm as any,
      name: code ? `Transaccion ${code}` : folder.name,
    };
  }

  const transaccionPropiaNorm = normalize<GenericTransaccion>(
    (folder as any).transaccion_propia,
    "codigo"
  );
  if (transaccionPropiaNorm?.length) {
    const first = transaccionPropiaNorm[0] as any;
    const code = first?.codigo ?? "";
    return {
      ...folder,
      transaccion_propia: transaccionPropiaNorm as any,
      name: code ? `Transaccion ${code}` : folder.name,
    };
  }

  return folder;
};